import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';

import { GatsbySanityImageFluid } from '../types';
import { truncateText, convertRToSuperscript } from '../utils/utils';
import ReadMoreLink from './ReadMoreLink';
import GenericLink from './GenericLink';

import * as styles from './NewsSection.module.scss';

interface NewsArticle {
  title: string;
  slug: {
    current: string;
  };
  date: string;
  extract: string;
  image: {
    asset: {
      fluid: GatsbySanityImageFluid;
      squareFluid: GatsbySanityImageFluid;
    };
  };
}

interface NewsArticleObject {
  newsArticleReference: NewsArticle;
  alternateSquareImage?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
}

interface QueryData {
  sanityHomepage: {
    // NewsArticleObject is the new format, but we might still get some data in the old format
    news: Array<NewsArticle | NewsArticleObject>;
  };
}

function isNewsArticle(newsArticle: NewsArticle | NewsArticleObject): newsArticle is NewsArticle {
  return (newsArticle as NewsArticle).title !== undefined;
}

const NewsSection = (): React.ReactElement => {
  const { t } = useTranslation();
  const data: QueryData = useStaticQuery(
    graphql`
      {
        sanityHomepage {
          news {
            newsArticleReference {
              title
              slug {
                current
              }
              date
              extract
              image {
                asset {
                  fluid(maxWidth: 840, maxHeight: 420) {
                    ...GatsbySanityImageFluid
                  }
                  squareFluid: fluid(maxWidth: 400, maxHeight: 400) {
                    ...GatsbySanityImageFluid
                  }
                }
              }
            }
            alternateSquareImage {
              asset {
                fluid(maxWidth: 400, maxHeight: 400) {
                  ...GatsbySanityImageFluid
                }
              }
            }
          }
        }
      }
    `,
  );

  // If articles are in old format, convert to new
  const newsArticles = data.sanityHomepage?.news.map(newsArticle =>
    isNewsArticle(newsArticle)
      ? {
          newsArticleReference: newsArticle,
        }
      : newsArticle,
  );

  return (
    <>
      <span className={styles.border}>
        <h2 className={styles.sectionTitle}>{t('news_page.title', 'News')}</h2>
      </span>
      <section className={styles.section}>
        {newsArticles?.map((newsArticle, i) => (
          <GenericLink
            noStyle
            key={i}
            to={`/news/${newsArticle.newsArticleReference.slug.current}`}
            className={styles.newsCard}
          >
            <Img
              className={styles.alternateSquareImage}
              fluid={
                newsArticle.alternateSquareImage
                  ? newsArticle.alternateSquareImage.asset.fluid
                  : newsArticle.newsArticleReference.image.asset.squareFluid
              }
              alt={newsArticle.newsArticleReference.title}
            />

            <Img
              className={styles.image}
              fluid={newsArticle.newsArticleReference.image.asset.fluid}
              alt={newsArticle.newsArticleReference.title}
            />

            <div className={styles.articleText}>
              <div className={styles.titleContainer}>
                <h4 className={styles.title} title={newsArticle.newsArticleReference.title}>
                  {convertRToSuperscript(truncateText(newsArticle.newsArticleReference.title, 100))}
                </h4>
                <span className={styles.date}>{newsArticle.newsArticleReference.date}</span>
              </div>
              <div className={styles.textContainer}>
                <span className={styles.extract}>
                  {convertRToSuperscript(
                    truncateText(newsArticle.newsArticleReference.extract, 250),
                  )}
                </span>
                <ReadMoreLink styleOnly className={styles.readMoreLink}>
                  {t('news_section.read_more_link', 'Read More')}
                </ReadMoreLink>
              </div>
            </div>
          </GenericLink>
        ))}
      </section>
      <span className={styles.border}></span>
    </>
  );
};

export default NewsSection;
